/**
 * MppConversionHelpModal — appears when a user drops a binary .mpp file.
 *
 * We don't have a reliable pure-JS MPP parser (the format is closed
 * binary; no working npm package does the conversion in-browser), so
 * the friendliest thing we can do is meet the user where they are:
 * show a 20-second visual guide for "File → Save As → XML" inside MS
 * Project. The modal also offers two safety nets:
 *
 *   1. **Try uploading anyway** — if the backend happens to have a
 *      Java runtime, the binary parser will work and we skip the guide.
 *   2. **Cancel** — closes the modal, keeps the user's chat state.
 */
import { X, FileText, ArrowRight, Info } from 'lucide-react';

export const MppConversionHelpModal = ({ open, fileName, onTryAnyway, onCancel }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(10,27,46,0.72)', backdropFilter: 'blur(8px)' }}
      data-testid="mpp-conversion-modal"
    >
      <div
        className="rounded-2xl overflow-hidden w-full max-w-lg"
        style={{ background: 'var(--nexus-bg-primary)', border: '1px solid var(--hairline)' }}
      >
        {/* Header */}
        <div className="px-6 py-4 flex items-start gap-3 border-b border-nexus-border-subtle">
          <div
            className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(242,107,31,0.15)', color: '#F26B1F' }}
          >
            <FileText size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>
              Binary MPP detected
            </div>
            <h3 className="font-heading text-base font-semibold text-nexus-text-primary mt-0.5 truncate">
              {fileName || 'project.mpp'}
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="flex-shrink-0 p-1.5 rounded hover:bg-nexus-bg-secondary text-nexus-text-tertiary"
            data-testid="mpp-modal-close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-[13px] text-nexus-text-secondary leading-relaxed">
            Binary MS Project files (<code className="text-[11.5px] px-1 py-0.5 rounded" style={{ background: 'var(--layer-1)' }}>.mpp</code>)
            can&apos;t always be parsed server-side. The fastest fix:
            <b className="text-nexus-text-primary"> export to XML inside Microsoft Project</b>.
          </p>

          {/* Step list */}
          <ol className="mt-4 space-y-2.5">
            {[
              ['Open the project in Microsoft Project.', 'Same file you just tried to upload.'],
              ['File → Save As → Browse to a folder you remember.', 'Cmd-S on Mac · Ctrl-S on Windows.'],
              ['In "Save as type", pick "XML Format (*.xml)".', 'NOT "MS Project Format" — pick XML.'],
              ['Drop the .xml file in chat instead.', 'Cognara parses XML schedules natively, no extra tools needed.'],
            ].map(([title, sub], idx) => (
              <li key={idx} className="flex gap-3" data-testid={`mpp-modal-step-${idx + 1}`}>
                <div
                  className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-mono text-[11px] font-semibold"
                  style={{ background: 'var(--layer-1)', color: '#F26B1F', border: '1px solid rgba(242,107,31,0.35)' }}
                >
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-nexus-text-primary">{title}</div>
                  <div className="text-[11.5px] text-nexus-text-tertiary leading-snug">{sub}</div>
                </div>
              </li>
            ))}
          </ol>

          {/* Primavera tip */}
          <div
            className="mt-4 rounded-lg p-3 flex items-start gap-2 text-[11.5px] leading-relaxed"
            style={{ background: 'var(--layer-1)', color: 'var(--nexus-text-secondary)' }}
          >
            <Info size={12} className="mt-0.5 flex-shrink-0 opacity-70" />
            <span>
              <b className="text-nexus-text-primary">Using Primavera P6?</b> Just upload the .xer
              file directly — no conversion needed.
            </span>
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-6 py-3 flex items-center justify-between gap-3 border-t border-nexus-border-subtle">
          <button
            onClick={onCancel}
            className="text-[12px] font-medium text-nexus-text-secondary hover:text-nexus-text-primary transition-colors"
            data-testid="mpp-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onTryAnyway}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-4 py-2 rounded-lg transition-all hover:opacity-90"
            style={{ background: '#F26B1F', color: '#fff' }}
            data-testid="mpp-modal-try-anyway"
          >
            Try uploading anyway <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MppConversionHelpModal;
